import React from 'react';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Tabs from '@mui/material/Tabs';
import Tab from '@mui/material/Tab';
import TextField from '@mui/material/TextField';
import Button from '@mui/material/Button';
import Paper from '@mui/material/Paper';
import Table from '@mui/material/Table';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import TableCell from '@mui/material/TableCell';
import TableBody from '@mui/material/TableBody';
import Checkbox from '@mui/material/Checkbox';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import MenuItem from '@mui/material/MenuItem';
import Select from '@mui/material/Select';
import InputLabel from '@mui/material/InputLabel';
import FormControl from '@mui/material/FormControl';
import Alert from '@mui/material/Alert';
import CircularProgress from '@mui/material/CircularProgress';
import IconButton from '@mui/material/IconButton';
import InfoIcon from '@mui/icons-material/InfoOutlined';
import DeleteIcon from '@mui/icons-material/DeleteOutline';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import { useAuth } from '../context/AuthContext';
import { VideoEntity, StoryBaseEntity } from '../types/entities';
import videoService, { S3FolderVideosResponse, S3VideoItem } from '../service/video.service';
import storyBaseService from '../service/story-base.service';
import videoMergeJobsService, { MergeInputVideoPayload, MergeGlobalSettingsPayload } from '../service/video-merge-jobs.service';

interface MergeRow {
  video: VideoEntity;
  index: number;
  durationMs: number;
  transitionType: string;
  transitionDurationMs: number;
}

const defaultGlobalSettings: MergeGlobalSettingsPayload = {
  fps: 30,
  container: 'mp4',
  audioCodec: 'aac',
  videoCodec: 'h264',
  transitionType: 'slide-right',
  resolutionWidth: 1920,
  resolutionHeight: 1080,
  transitionDurationMs: 3000,
};

const VideoMergePage: React.FC = () => {
  const { user } = useAuth();
  const [tab, setTab] = React.useState(0);
  const [videos, setVideos] = React.useState<VideoEntity[]>([]);
  const [titleMap, setTitleMap] = React.useState<Record<string, string>>({});
  const [languageMap, setLanguageMap] = React.useState<Record<string, string>>({});
  const [loadingVideos, setLoadingVideos] = React.useState(false);
  const [videoError, setVideoError] = React.useState<string | null>(null);
  const [search, setSearch] = React.useState('');
  const [selectedIds, setSelectedIds] = React.useState<string[]>([]);
  const [mergeDialogOpen, setMergeDialogOpen] = React.useState(false);
  const [mergeRows, setMergeRows] = React.useState<MergeRow[]>([]);
  const [dragIndex, setDragIndex] = React.useState<number | null>(null);
  const [globalSettings, setGlobalSettings] = React.useState<MergeGlobalSettingsPayload>(defaultGlobalSettings);
  const [submitting, setSubmitting] = React.useState(false);
  const [resultDialogOpen, setResultDialogOpen] = React.useState(false);
  const [resultMessage, setResultMessage] = React.useState('');
  const [jobs, setJobs] = React.useState<any[]>([]);
  const [loadingJobs, setLoadingJobs] = React.useState(false);
  const [jobsError, setJobsError] = React.useState<string | null>(null);
  const [selectedJob, setSelectedJob] = React.useState<any | null>(null);
  const [jobTitle, setJobTitle] = React.useState<string>('');
  const [s3Loading, setS3Loading] = React.useState(false);
  const [s3Error, setS3Error] = React.useState<string | null>(null);
  const [s3Data, setS3Data] = React.useState<S3FolderVideosResponse | null>(null);
  const [s3SelectedUrls, setS3SelectedUrls] = React.useState<string[]>([]);
  const [s3Tab, setS3Tab] = React.useState(0);
  const [s3FinalVideos, setS3FinalVideos] = React.useState<S3VideoItem[]>([]);
  const [s3MergedVideos, setS3MergedVideos] = React.useState<S3VideoItem[]>([]);
  const [s3FinalLoading, setS3FinalLoading] = React.useState(false);
  const [s3MergedLoading, setS3MergedLoading] = React.useState(false);
  const [s3FinalError, setS3FinalError] = React.useState<string | null>(null);
  const [s3MergedError, setS3MergedError] = React.useState<string | null>(null);
  const [savingVideoUrl, setSavingVideoUrl] = React.useState<string | null>(null);
  const [videoModalOpen, setVideoModalOpen] = React.useState(false);
  const [videoModalUrl, setVideoModalUrl] = React.useState<string | null>(null);
  const [saveFormOpen, setSaveFormOpen] = React.useState(false);
  const [saveFormUrl, setSaveFormUrl] = React.useState<string | null>(null);
  const [saveFormData, setSaveFormData] = React.useState({ title: '', languageCode: 'TR', type: 'youtube', lastModified: '' });
  const [savedVideoUrls, setSavedVideoUrls] = React.useState<string[]>([]);
  const [awsSavedVideos, setAwsSavedVideos] = React.useState<any[]>([]);
  const [awsSavedLoading, setAwsSavedLoading] = React.useState(false);
  const [awsSavedError, setAwsSavedError] = React.useState<string | null>(null);
  const [awsSavedSelectedIds, setAwsSavedSelectedIds] = React.useState<string[]>([]);
  const [jobsSearch, setJobsSearch] = React.useState('');
  const [deleteDialogOpen, setDeleteDialogOpen] = React.useState(false);
  const [jobToDelete, setJobToDelete] = React.useState<any | null>(null);
  const [deleteSubmitting, setDeleteSubmitting] = React.useState(false);
  const [deleteError, setDeleteError] = React.useState<string | null>(null);

  const loadVideos = React.useCallback(async () => {
    if (!user?.id) return;
    setLoadingVideos(true);
    setVideoError(null);
    try {
      const allItems: VideoEntity[] = [];
      let page = 1;
      const size = 100;
      let hasMore = true;

      while (hasMore) {
        const res = await videoService.getVideosByMember(user.id, page, size);
        const items = res.items || [];
        allItems.push(...items);
        hasMore = !!res.hasMore;
        page += 1;
        if (!items.length) break;
      }

      setVideos(allItems);

      const basesCache: Record<string, StoryBaseEntity> = {} as any;
      const nextTitleMap: Record<string, string> = {};
      const nextLangMap: Record<string, string> = {};
      for (const v of allItems) {
        const baseId = v.storyBaseId || '';
        if (!baseId) {
          nextTitleMap[v.id] = '';
          nextLangMap[v.id] = '';
          continue;
        }
        try {
          const base = basesCache[baseId] || (basesCache[baseId] = await storyBaseService.getStoryBase(baseId));
          nextTitleMap[v.id] = String(base?.title || '');
          nextLangMap[v.id] = String(base?.language || '');
        } catch {
          nextTitleMap[v.id] = '';
          nextLangMap[v.id] = '';
        }
      }
      setTitleMap(nextTitleMap);
      setLanguageMap(nextLangMap);
    } catch (e: any) {
      setVideoError(e?.response?.data?.message || e?.message || 'Failed to load videos');
    } finally {
      setLoadingVideos(false);
    }
  }, [user?.id]);

  const loadJobs = React.useCallback(async () => {
    setLoadingJobs(true);
    setJobsError(null);
    try {
      const data = await videoMergeJobsService.getAllMergeJobs();
      setJobs(Array.isArray(data) ? data : []);
    } catch (e: any) {
      setJobsError(e?.response?.data?.message || e?.message || 'Failed to load merge jobs');
    } finally {
      setLoadingJobs(false);
    }
  }, []);

  React.useEffect(() => {
    loadVideos();
  }, [loadVideos]);

  React.useEffect(() => {
    if (tab !== 1) return;
    let timer: any;
    const tick = async () => {
      await loadJobs();
    };
    tick();
    timer = setInterval(tick, 10000);
    return () => {
      if (timer) clearInterval(timer);
    };
  }, [tab, loadJobs]);

  const filteredJobs = React.useMemo(() => {
    const q = jobsSearch.trim().toLowerCase();
    if (!q) return jobs;
    return jobs.filter((j) => (j.title || '').toLowerCase().includes(q));
  }, [jobs, jobsSearch]);

  const filteredVideos = React.useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return videos;
    return videos.filter((v) => (titleMap[v.id] || '').toLowerCase().includes(q));
  }, [videos, search, titleMap]);

  const toggleSelect = (id: string) => {
    setSelectedIds((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));
  };

  const openMergeDialog = () => {
    const rows: MergeRow[] = selectedIds
      .map((id, idx) => {
        const v = videos.find((x) => x.id === id);
        if (!v) return null;
        return {
          video: v,
          index: idx + 1,
          durationMs: 2000,
          transitionType: 'slide-right',
          transitionDurationMs: 1400,
        } as MergeRow;
      })
      .filter(Boolean) as MergeRow[];
    setMergeRows(rows);
    // Varsayılan title: ikinci videonun baseStory.title'ı
    const second = rows[1];
    const defaultTitle = second ? (titleMap[second.video.id] || '') : '';
    setJobTitle(defaultTitle);
    setGlobalSettings(defaultGlobalSettings);
    setMergeDialogOpen(true);
  };

  const handleMergeRowChange = (rowIndex: number, field: keyof MergeRow, value: any) => {
    setMergeRows((prev) => {
      const next = [...prev];
      const row = { ...next[rowIndex] };
      (row as any)[field] = value;
      next[rowIndex] = row;
      return next;
    });
  };

  const handleGlobalChange = (field: keyof MergeGlobalSettingsPayload, value: any) => {
    setGlobalSettings((prev) => ({ ...prev, [field]: value }));
  };

  const onDragStart = (index: number) => {
    setDragIndex(index);
  };

  const onDrop = (index: number) => {
    setMergeRows((prev) => {
      if (dragIndex === null || dragIndex === index) return prev;
      const next = [...prev];
      const [moved] = next.splice(dragIndex, 1);
      next.splice(index, 0, moved);
      return next.map((r, i) => ({ ...r, index: i + 1 }));
    });
    setDragIndex(null);
  };

  const canSubmitMerge = React.useMemo(() => {
    if (mergeRows.length < 2) return false;
    if (!globalSettings.fps || !globalSettings.container || !globalSettings.videoCodec || !globalSettings.audioCodec) return false;
    return mergeRows.every((r) => r.durationMs > 0 && r.transitionDurationMs >= 0 && !!r.transitionType);
  }, [mergeRows, globalSettings]);

  const handleSubmitMerge = async () => {
    if (!canSubmitMerge) return;
    setSubmitting(true);
    try {
      const firstRunId = mergeRows[0]?.video.storyRunId || null;
      const inputVideos: MergeInputVideoPayload[] = mergeRows.map((r) => ({
        url: r.video.videoUrl,
        type: 'video',
        index: r.index,
        durationMs: r.durationMs,
        transitionType: r.transitionType,
        transitionDurationMs: r.transitionDurationMs,
        orderNo: r.video.orderNo ?? null,
        runId: r.video.storyRunId || firstRunId || null,
      }));
      const res = await videoMergeJobsService.createMergeJob({
        inputVideos,
        globalSettings,
        title: (jobTitle || '').trim() || null,
      });
      setResultMessage(res?.isNew ? 'Yeni merge işi oluşturuldu.' : 'Aynı ayarlarla mevcut bir merge işi zaten var.');
      setResultDialogOpen(true);
      setMergeDialogOpen(false);
      setSelectedIds([]);
      await loadJobs();
    } catch (e: any) {
      setResultMessage(e?.response?.data?.message || e?.message || 'Merge isteği başarısız oldu');
      setResultDialogOpen(true);
    } finally {
      setSubmitting(false);
    }
  };

  const handleLoadS3Videos = async () => {
    setS3Loading(true);
    setS3Error(null);
    try {
      const data = await videoService.getS3VideosByFolderPrefix('videos/notebooklm-videos');
      setS3Data(data);
      setS3SelectedUrls([]);
    } catch (e: any) {
      setS3Error(e?.response?.data?.message || e?.message || 'S3 videoları yüklenemedi');
    } finally {
      setS3Loading(false);
    }
  };

  const toggleSelectS3Url = (url: string) => {
    setS3SelectedUrls((prev) => (prev.includes(url) ? prev.filter((x) => x !== url) : [...prev, url]));
  };

  const openMergeDialogFromS3 = () => {
    const basePrefix = (s3Data?.prefix || 'videos/notebooklm-videos/').replace(/^\/+/, '');

    const nextTitleMap: Record<string, string> = { ...titleMap };
    const rows: MergeRow[] = s3SelectedUrls.map((rawUrl, idx) => {
      const url = encodeURI(rawUrl);
      const idxPrefix = url.indexOf(basePrefix);
      const display = idxPrefix >= 0 ? url.substring(idxPrefix + basePrefix.length) : url;
      nextTitleMap[url] = display;

      return {
        // S3 videoları için minimal VideoEntity benzeri nesne
        video: {
          id: url,
          videoUrl: url,
          orderNo: null,
          storyRunId: null,
        } as any,
        index: idx + 1,
        durationMs: 2000,
        transitionType: 'slide-right',
        transitionDurationMs: 1400,
      } as MergeRow;
    });
    const firstUrl = s3SelectedUrls[0] ? encodeURI(s3SelectedUrls[0]) : '';
    let defaultTitle = '';
    if (firstUrl) {
      const idx = firstUrl.indexOf(basePrefix);
      defaultTitle = idx >= 0 ? firstUrl.substring(idx + basePrefix.length) : firstUrl;
    }
    setTitleMap(nextTitleMap);
    setMergeRows(rows);
    setJobTitle(defaultTitle);
    setGlobalSettings(defaultGlobalSettings);
    setMergeDialogOpen(true);
  };

  const renderMergeTab = () => (
    <Box>
      <Box sx={{ display: 'flex', gap: 2, mb: 2, alignItems: 'center' }}>
        <TextField
          label="Search by title"
          size="small"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
        <Button
          variant="contained"
          disabled={selectedIds.length < 2}
          onClick={openMergeDialog}
        >
          Merge
        </Button>
      </Box>
      {loadingVideos && (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
          <CircularProgress size={20} />
          <Typography>Videolar yükleniyor...</Typography>
        </Box>
      )}
      {!loadingVideos && videoError && <Alert severity="error" sx={{ mb: 2 }}>{videoError}</Alert>}
      <Paper sx={{ width: '100%', overflowX: 'auto' }}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell padding="checkbox" />
              <TableCell>Title</TableCell>
              <TableCell>Order No</TableCell>
              <TableCell>Created At</TableCell>
              <TableCell>Language</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {filteredVideos.map((v) => (
              <TableRow key={v.id} hover>
                <TableCell padding="checkbox">
                  <Checkbox
                    checked={selectedIds.includes(v.id)}
                    onChange={() => toggleSelect(v.id)}
                  />
                </TableCell>
                <TableCell>{titleMap[v.id] || '-'}</TableCell>
                <TableCell>{v.orderNo ?? '-'}</TableCell>
                <TableCell>{new Date(v.createdAt).toLocaleString()}</TableCell>
                <TableCell>{languageMap[v.id] || '-'}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Paper>
    </Box>
  );

  const loadSavedVideoUrls = () => {
    try {
      const saved = localStorage.getItem('savedVideoUrls');
      if (saved) {
        setSavedVideoUrls(JSON.parse(saved));
      }
    } catch (e) {
      console.error('Failed to load saved video URLs', e);
    }
  };

  React.useEffect(() => {
    loadSavedVideoUrls();
  }, []);

  const handleLoadS3FinalVideos = async () => {
    setS3FinalLoading(true);
    setS3FinalError(null);
    try {
      const data = await videoService.getS3FinalVideos();
      setS3FinalVideos(data || []);
    } catch (e: any) {
      setS3FinalError(e?.response?.data?.message || e?.message || 'Final videoları yüklenemedi');
    } finally {
      setS3FinalLoading(false);
    }
  };

  const handleLoadS3MergedVideos = async () => {
    setS3MergedLoading(true);
    setS3MergedError(null);
    try {
      const data = await videoService.getS3MergedVideos();
      setS3MergedVideos(data || []);
    } catch (e: any) {
      setS3MergedError(e?.response?.data?.message || e?.message || 'Merged videoları yüklenemedi');
    } finally {
      setS3MergedLoading(false);
    }
  };

  const handleLoadAwsSavedVideos = async () => {
    setAwsSavedLoading(true);
    setAwsSavedError(null);
    try {
      const data = await videoService.getAwsSavedVideos();
      setAwsSavedVideos(data || []);
    } catch (e: any) {
      setAwsSavedError(e?.response?.data?.message || e?.message || 'AWS saved videoları yüklenemedi');
    } finally {
      setAwsSavedLoading(false);
    }
  };

  const handleDeleteAwsSavedVideo = async (id: string) => {
    if (!window.confirm('Bu videoyu silmek istediğinizden emin misiniz?')) return;
    try {
      await videoService.deleteAwsSavedVideo(id);
      await handleLoadAwsSavedVideos();
    } catch (e: any) {
      alert(e?.response?.data?.message || e?.message || 'Video silinemedi');
    }
  };

  const handleToggleAwsSavedVideo = (id: string) => {
    setAwsSavedSelectedIds((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
    );
  };

  const handleToggleAllAwsSavedVideos = () => {
    if (awsSavedSelectedIds.length === awsSavedVideos.length) {
      setAwsSavedSelectedIds([]);
    } else {
      setAwsSavedSelectedIds(awsSavedVideos.map((v) => v.id));
    }
  };

  const handleMergeAwsSavedVideos = () => {
    if (awsSavedSelectedIds.length < 2) {
      alert('En az 2 video seçmelisiniz');
      return;
    }
    // AWS saved videos'ı VideoEntity formatına dönüştür
    const selectedVideos = awsSavedVideos
      .filter((v) => awsSavedSelectedIds.includes(v.id))
      .map((v) => ({
        id: v.id,
        videoUrl: v.videoUrl,
        memberId: user?.id || '',
        storyBaseId: null,
        storyRunId: null,
        orderNo: v.orderNo,
        createdAt: v.createdAt,
      }));

    const rows: MergeRow[] = selectedVideos
      .map((v, idx) => {
        return {
          video: v as VideoEntity,
          index: idx + 1,
          durationMs: 2000,
          transitionType: 'slide-right',
          transitionDurationMs: 1400,
        } as MergeRow;
      })
      .filter(Boolean) as MergeRow[];
    
    setMergeRows(rows);
    // Title map'i güncelle
    const newTitleMap: Record<string, string> = {};
    awsSavedVideos.forEach((v) => {
      if (awsSavedSelectedIds.includes(v.id)) {
        newTitleMap[v.id] = v.title;
      }
    });
    setTitleMap((prev) => ({ ...prev, ...newTitleMap }));
    
    const second = rows[1];
    const defaultTitle = second ? (awsSavedVideos.find(v => v.id === second.video.id)?.title || '') : '';
    setJobTitle(defaultTitle);
    setGlobalSettings(defaultGlobalSettings);
    setMergeDialogOpen(true);
  };

  const handleOpenSaveForm = (videoUrl: string, lastModified?: string) => {
    const fileName = videoUrl.split('/').pop() || '';
    
    setSaveFormUrl(videoUrl);
    setSaveFormData({
      title: fileName.replace(/\.(mp4|mov|avi)$/i, ''),
      languageCode: 'TR',
      type: 'youtube',
      lastModified: lastModified || new Date().toISOString(),
    });
    setSaveFormOpen(true);
  };

  const handleSaveVideoToDatabase = async () => {
    if (!saveFormUrl) return;
    
    setSavingVideoUrl(saveFormUrl);
    try {
      await videoService.createAwsSavedVideo({
        title: saveFormData.title,
        languageCode: saveFormData.languageCode,
        type: saveFormData.type,
        videoUrl: saveFormUrl,
        createdAt: saveFormData.lastModified,
      });
      
      // localStorage'a ekle
      const updatedUrls = [...savedVideoUrls, saveFormUrl];
      setSavedVideoUrls(updatedUrls);
      localStorage.setItem('savedVideoUrls', JSON.stringify(updatedUrls));
      
      setSaveFormOpen(false);
    } catch (e: any) {
      alert(e?.response?.data?.message || e?.message || 'Video kaydedilemedi');
    } finally {
      setSavingVideoUrl(null);
    }
  };

  const handleOpenVideoModal = (url: string) => {
    setVideoModalUrl(url);
    setVideoModalOpen(true);
  };

  const handleCloseVideoModal = () => {
    setVideoModalOpen(false);
    setVideoModalUrl(null);
  };

  const renderAwsSavedVideosTab = () => (
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
        <Button variant="contained" onClick={handleLoadAwsSavedVideos}>
          Kaydedilmiş Videoları Getir
        </Button>
        {awsSavedSelectedIds.length >= 2 && (
          <Button variant="contained" color="secondary" onClick={handleMergeAwsSavedVideos}>
            Merge ({awsSavedSelectedIds.length} video)
          </Button>
        )}
        {awsSavedLoading && <CircularProgress size={20} />}
      </Box>

      {awsSavedError && (
        <Alert severity="error" sx={{ mb: 2 }}>{awsSavedError}</Alert>
      )}

      {!awsSavedLoading && awsSavedVideos.length > 0 && (
        <Paper sx={{ width: '100%', overflowX: 'auto' }}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell padding="checkbox">
                  <Checkbox
                    checked={awsSavedSelectedIds.length === awsSavedVideos.length && awsSavedVideos.length > 0}
                    indeterminate={awsSavedSelectedIds.length > 0 && awsSavedSelectedIds.length < awsSavedVideos.length}
                    onChange={handleToggleAllAwsSavedVideos}
                  />
                </TableCell>
                <TableCell>Title</TableCell>
                <TableCell>Language</TableCell>
                <TableCell>Type</TableCell>
                <TableCell>Order No</TableCell>
                <TableCell>Created At</TableCell>
                <TableCell>Saved At</TableCell>
                <TableCell>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {awsSavedVideos.map((video) => (
                <TableRow key={video.id} hover>
                  <TableCell padding="checkbox">
                    <Checkbox
                      checked={awsSavedSelectedIds.includes(video.id)}
                      onChange={() => handleToggleAwsSavedVideo(video.id)}
                    />
                  </TableCell>
                  <TableCell>{video.title}</TableCell>
                  <TableCell>{video.languageCode}</TableCell>
                  <TableCell>{video.type}</TableCell>
                  <TableCell>{video.orderNo}</TableCell>
                  <TableCell>{new Date(video.createdAt).toLocaleString()}</TableCell>
                  <TableCell>{new Date(video.savedAt).toLocaleString()}</TableCell>
                  <TableCell>
                    <Box sx={{ display: 'flex', gap: 1 }}>
                      <Button
                        variant="outlined"
                        size="small"
                        onClick={() => handleOpenVideoModal(video.videoUrl)}
                      >
                        İzle
                      </Button>
                      <IconButton
                        size="small"
                        color="error"
                        onClick={() => handleDeleteAwsSavedVideo(video.id)}
                      >
                        <DeleteIcon />
                      </IconButton>
                    </Box>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Paper>
      )}

      {!awsSavedLoading && awsSavedVideos.length === 0 && (
        <Typography color="text.secondary">Henüz kaydedilmiş video yok. "Getir" butonuna tıklayın.</Typography>
      )}
    </Box>
  );

  const renderS3Tab = () => (
    <Box>
      <Tabs value={s3Tab} onChange={(_, v) => setS3Tab(v)} sx={{ mb: 2 }}>
        <Tab label="Final Videos" />
        <Tab label="Merged Videos" />
      </Tabs>

      {s3Tab === 0 && (
        <Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
            <Button
              variant="contained"
              onClick={handleLoadS3FinalVideos}
              disabled={s3FinalLoading}
            >
              {s3FinalLoading ? 'Yükleniyor...' : 'Final Videoları Getir'}
            </Button>
            <Typography variant="body2" color="text.secondary">
              {s3FinalVideos.length > 0 ? `${s3FinalVideos.length} video bulundu` : ''}
            </Typography>
          </Box>
          {s3FinalError && <Alert severity="error" sx={{ mb: 2 }}>{s3FinalError}</Alert>}
          {s3FinalVideos.length > 0 && (
            <Paper sx={{ width: '100%', overflowX: 'auto' }}>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>File Name</TableCell>
                    <TableCell>Size</TableCell>
                    <TableCell>Last Modified</TableCell>
                    <TableCell>Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {s3FinalVideos.map((video) => {
                    const fileName = video.key.split('/').pop() || video.key;
                    const sizeInMB = (video.size / (1024 * 1024)).toFixed(2);
                    const isSaving = savingVideoUrl === video.url;
                    const isSaved = savedVideoUrls.includes(video.url);
                    return (
                      <TableRow key={video.key} hover>
                        <TableCell>{fileName}</TableCell>
                        <TableCell>{sizeInMB} MB</TableCell>
                        <TableCell>{new Date(video.lastModified).toLocaleString()}</TableCell>
                        <TableCell>
                          <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                            <Button
                              variant="outlined"
                              size="small"
                              onClick={() => handleOpenVideoModal(video.url)}
                            >
                              İzle
                            </Button>
                            <Button
                              variant="contained"
                              size="small"
                              color="success"
                              disabled={isSaving}
                              onClick={() => handleOpenSaveForm(video.url, video.lastModified)}
                            >
                              Kaydet
                            </Button>
                            {isSaved && (
                              <CheckCircleIcon sx={{ color: 'success.main', ml: 1 }} />
                            )}
                          </Box>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </Paper>
          )}
        </Box>
      )}

      {s3Tab === 1 && (
        <Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
            <Button
              variant="contained"
              onClick={handleLoadS3MergedVideos}
              disabled={s3MergedLoading}
            >
              {s3MergedLoading ? 'Yükleniyor...' : 'Merged Videoları Getir'}
            </Button>
            <Typography variant="body2" color="text.secondary">
              {s3MergedVideos.length > 0 ? `${s3MergedVideos.length} video bulundu` : ''}
            </Typography>
          </Box>
          {s3MergedError && <Alert severity="error" sx={{ mb: 2 }}>{s3MergedError}</Alert>}
          {s3MergedVideos.length > 0 && (
            <Paper sx={{ width: '100%', overflowX: 'auto' }}>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>File Name</TableCell>
                    <TableCell>Size</TableCell>
                    <TableCell>Last Modified</TableCell>
                    <TableCell>Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {s3MergedVideos.map((video) => {
                    const fileName = video.key.split('/').pop() || video.key;
                    const sizeInMB = (video.size / (1024 * 1024)).toFixed(2);
                    const isSaving = savingVideoUrl === video.url;
                    const isSaved = savedVideoUrls.includes(video.url);
                    return (
                      <TableRow key={video.key} hover>
                        <TableCell>{fileName}</TableCell>
                        <TableCell>{sizeInMB} MB</TableCell>
                        <TableCell>{new Date(video.lastModified).toLocaleString()}</TableCell>
                        <TableCell>
                          <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                            <Button
                              variant="outlined"
                              size="small"
                              onClick={() => handleOpenVideoModal(video.url)}
                            >
                              İzle
                            </Button>
                            <Button
                              variant="contained"
                              size="small"
                              color="success"
                              disabled={isSaving}
                              onClick={() => handleOpenSaveForm(video.url, video.lastModified)}
                            >
                              Kaydet
                            </Button>
                            {isSaved && (
                              <CheckCircleIcon sx={{ color: 'success.main', ml: 1 }} />
                            )}
                          </Box>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </Paper>
          )}
        </Box>
      )}
    </Box>
  );

  const renderJobsTab = () => (
    <Box>
      <Box sx={{ display: 'flex', gap: 2, mb: 2, alignItems: 'center' }}>
        <TextField
          label="Search by title"
          size="small"
          value={jobsSearch}
          onChange={(e) => setJobsSearch(e.target.value)}
        />
      </Box>
      {loadingJobs && (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
          <CircularProgress size={20} />
          <Typography>Merge işeri yükleniyor...</Typography>
        </Box>
      )}
      {!loadingJobs && jobsError && (
        <Alert severity="error" sx={{ mb: 2 }}>{jobsError}</Alert>
      )}
      {!loadingJobs && !jobsError && jobs.length > 0 && (
        (() => {
          const active = jobs.filter((j) => j.status === 'pending' || j.status === 'in_progress');
          if (active.length === 0) return null;
          return (
            <Alert severity="info" sx={{ mb: 2 }}>
              <Box>
                <Typography variant="subtitle2">Videolarınız merge ediliyor:</Typography>
                {active.map((j: any) => (
                  <Box key={j.id} sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Typography variant="body2">
                      #{j.id} - status: {j.status}
                    </Typography>
                    <IconButton
                      size="small"
                      aria-label="Detay"
                      onClick={() => setSelectedJob(j)}
                    >
                      <InfoIcon fontSize="small" />
                    </IconButton>
                  </Box>
                ))}
              </Box>
            </Alert>
          );
        })()
      )}
      <Paper sx={{ width: '100%', overflowX: 'auto' }}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Status</TableCell>
              <TableCell>Title</TableCell>
              <TableCell>Output Video</TableCell>
              <TableCell>Created At</TableCell>
              <TableCell>Run Id</TableCell>
              <TableCell align="right">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {filteredJobs.map((j) => (
              <TableRow key={j.id}>
                <TableCell>{j.status}</TableCell>
                <TableCell>{j.title || '-'}</TableCell>
                <TableCell>
                  {j.outputVideoUrl ? (
                    <Button
                      variant="outlined"
                      size="small"
                      onClick={() => window.open(j.outputVideoUrl, '_blank')}
                    >
                      Videoyu Aç
                    </Button>
                  ) : (
                    '-'
                  )}
                </TableCell>
                <TableCell>{j.createdAt ? new Date(j.createdAt).toLocaleString() : '-'}</TableCell>
                <TableCell>{j.runId || '-'}</TableCell>
                <TableCell align="right">
                  <IconButton
                    size="small"
                    aria-label="Sil"
                    disabled={deleteSubmitting}
                    onClick={() => {
                      setJobToDelete(j);
                      setDeleteError(null);
                      setDeleteDialogOpen(true);
                    }}
                  >
                    <DeleteIcon fontSize="small" />
                  </IconButton>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Paper>
    </Box>
  );

  return (
    <Box sx={{ p: 2 }}>
      <Typography variant="h5" sx={{ mb: 2 }}>Video Merge</Typography>
      <Tabs value={tab} onChange={(_, v) => setTab(v)} sx={{ mb: 2 }}>
        <Tab label="Merge Videos" />
        <Tab label="Merged Videos" />
        <Tab label="S3 Videos" />
        <Tab label="AWS Saved Videos" />
      </Tabs>
      {tab === 0 && renderMergeTab()}
      {tab === 1 && renderJobsTab()}
      {tab === 2 && renderS3Tab()}
      {tab === 3 && renderAwsSavedVideosTab()}

      <Dialog open={mergeDialogOpen} onClose={() => setMergeDialogOpen(false)} fullWidth maxWidth="md">
        <DialogTitle>Merge Settings</DialogTitle>
        <DialogContent>
          <TextField
            label="Title"
            fullWidth
            size="small"
            sx={{ mt: 1, mb: 2 }}
            value={jobTitle}
            onChange={(e) => setJobTitle(e.target.value)}
          />

          <Typography variant="subtitle1" sx={{ mt: 1, mb: 1 }}>Selected Videos</Typography>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Order</TableCell>
                <TableCell>Title</TableCell>
                <TableCell>Duration (ms)</TableCell>
                <TableCell>Transition Type</TableCell>
                <TableCell>Transition Duration (ms)</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {mergeRows.map((r, idx) => (
                <TableRow
                  key={r.video.id}
                  draggable
                  onDragStart={() => onDragStart(idx)}
                  onDragOver={(e) => e.preventDefault()}
                  onDrop={() => onDrop(idx)}
                >
                  <TableCell>{r.index}</TableCell>
                  <TableCell>{titleMap[r.video.id] || '-'}</TableCell>
                  <TableCell>
                    <TextField
                      type="number"
                      size="small"
                      value={r.durationMs}
                      onChange={(e) => handleMergeRowChange(idx, 'durationMs', Number(e.target.value) || 0)}
                    />
                  </TableCell>
                  <TableCell>
                    <FormControl size="small" fullWidth>
                      <Select
                        value={r.transitionType}
                        onChange={(e) => handleMergeRowChange(idx, 'transitionType', e.target.value)}
                      >
                        <MenuItem value="slide-right">slide-right</MenuItem>
                        <MenuItem value="slide-left">slide-left</MenuItem>
                        <MenuItem value="crossfade">crossfade</MenuItem>
                        <MenuItem value="fade">fade</MenuItem>
                        <MenuItem value="none">none</MenuItem>
                      </Select>
                    </FormControl>
                  </TableCell>
                  <TableCell>
                    <TextField
                      type="number"
                      size="small"
                      value={r.transitionDurationMs}
                      onChange={(e) => handleMergeRowChange(idx, 'transitionDurationMs', Number(e.target.value) || 0)}
                    />
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>

          <Typography variant="subtitle1" sx={{ mt: 3, mb: 1 }}>Global Settings</Typography>
          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 2 }}>
            <TextField
              label="FPS"
              type="number"
              size="small"
              value={globalSettings.fps}
              onChange={(e) => handleGlobalChange('fps', Number(e.target.value) || 0)}
            />
            <TextField
              label="Container"
              size="small"
              value={globalSettings.container}
              onChange={(e) => handleGlobalChange('container', e.target.value)}
            />
            <TextField
              label="Audio Codec"
              size="small"
              value={globalSettings.audioCodec}
              onChange={(e) => handleGlobalChange('audioCodec', e.target.value)}
            />
            <TextField
              label="Video Codec"
              size="small"
              value={globalSettings.videoCodec}
              onChange={(e) => handleGlobalChange('videoCodec', e.target.value)}
            />
            <FormControl size="small">
              <InputLabel id="transition-type-label">Transition Type</InputLabel>
              <Select
                labelId="transition-type-label"
                label="Transition Type"
                value={globalSettings.transitionType}
                onChange={(e) => handleGlobalChange('transitionType', e.target.value)}
              >
                <MenuItem value="slide-right">slide-right</MenuItem>
                <MenuItem value="slide-left">slide-left</MenuItem>
                <MenuItem value="crossfade">crossfade</MenuItem>
                <MenuItem value="fade">fade</MenuItem>
                <MenuItem value="none">none</MenuItem>
              </Select>
            </FormControl>
            <TextField
              label="Resolution Width"
              type="number"
              size="small"
              value={globalSettings.resolutionWidth}
              onChange={(e) => handleGlobalChange('resolutionWidth', Number(e.target.value) || 0)}
            />
            <TextField
              label="Resolution Height"
              type="number"
              size="small"
              value={globalSettings.resolutionHeight}
              onChange={(e) => handleGlobalChange('resolutionHeight', Number(e.target.value) || 0)}
            />
            <TextField
              label="Transition Duration (ms)"
              type="number"
              size="small"
              value={globalSettings.transitionDurationMs}
              onChange={(e) => handleGlobalChange('transitionDurationMs', Number(e.target.value) || 0)}
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setMergeDialogOpen(false)}>İptal</Button>
          <Button
            variant="contained"
            disabled={!canSubmitMerge || submitting}
            onClick={handleSubmitMerge}
          >
            {submitting ? 'Gönderiliyor...' : 'Merge'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={resultDialogOpen} onClose={() => setResultDialogOpen(false)}>
        <DialogTitle>Merge Sonucu</DialogTitle>
        <DialogContent>
          <Typography>{resultMessage}</Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setResultDialogOpen(false)}>Kapat</Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={!!selectedJob}
        onClose={() => setSelectedJob(null)}
        fullWidth
        maxWidth="md"
      >
        <DialogTitle>Merge Job Detayı</DialogTitle>
        <DialogContent>
          {selectedJob && (
            <Box sx={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word', fontFamily: 'monospace', fontSize: 12 }}>
              <Typography variant="subtitle2" sx={{ mb: 1 }}>Status: {selectedJob.status}</Typography>
              <Typography variant="subtitle2" sx={{ mb: 1 }}>Output Video: {selectedJob.outputVideoUrl || '-'}</Typography>
              <Typography variant="subtitle2" sx={{ mt: 2 }}>Input Videos</Typography>
              <Box component="pre" sx={{ mb: 2 }}>
                {JSON.stringify(selectedJob.inputVideos, null, 2)}
              </Box>
              <Typography variant="subtitle2" sx={{ mt: 2 }}>Global Settings</Typography>
              <Box component="pre">
                {JSON.stringify(selectedJob.globalSettings, null, 2)}
              </Box>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setSelectedJob(null)}>Kapat</Button>
        </DialogActions>
      </Dialog>
      <Dialog
        open={deleteDialogOpen}
        onClose={() => {
          if (deleteSubmitting) return;
          setDeleteDialogOpen(false);
          setJobToDelete(null);
          setDeleteError(null);
        }}
      >
        <DialogTitle>Videoyu silmek istediğinizden emin misiniz?</DialogTitle>
        <DialogContent>
          {deleteError && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {deleteError}
            </Alert>
          )}
          <Typography>
            Bu işlem, seçili merge işine ait output videoyu AWS S3 üzerinden silecek ve ilgili kayıtları kaldıracaktır.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => {
              if (deleteSubmitting) return;
              setDeleteDialogOpen(false);
              setJobToDelete(null);
              setDeleteError(null);
            }}
          >
            İptal
          </Button>
          <Button
            color="error"
            variant="contained"
            disabled={deleteSubmitting || !jobToDelete}
            onClick={async () => {
              if (!jobToDelete) return;
              try {
                setDeleteSubmitting(true);
                setDeleteError(null);

                if (jobToDelete.outputVideoUrl) {
                  const res = await videoService.deleteVideoByUrl(jobToDelete.outputVideoUrl);
                  if (!res.deleted) {
                    setDeleteError('Silme işlemi gerçekleştirilemedi.');
                    setDeleteSubmitting(false);
                    return;
                  }
                } else {
                  const res = await videoMergeJobsService.deleteMergeJob(jobToDelete.id);
                  if (!res.deleted) {
                    setDeleteError('Silme işlemi gerçekleştirilemedi.');
                    setDeleteSubmitting(false);
                    return;
                  }
                }

                setJobs((prev) => prev.filter((x) => x.id !== jobToDelete.id));
                setDeleteDialogOpen(false);
                setJobToDelete(null);
              } catch (e: any) {
                setDeleteError(e?.response?.data?.message || e?.message || 'Silme işlemi başarısız oldu');
              } finally {
                setDeleteSubmitting(false);
              }
            }}
          >
            {deleteSubmitting ? 'Siliniyor...' : 'Evet, Sil'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={videoModalOpen}
        onClose={handleCloseVideoModal}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          Video
          <IconButton
            aria-label="close"
            onClick={handleCloseVideoModal}
            sx={{
              position: 'absolute',
              right: 8,
              top: 8,
            }}
          >
            <DeleteIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          {videoModalUrl && (
            <Box sx={{ width: '100%', aspectRatio: '16/9' }}>
              <video
                src={videoModalUrl}
                controls
                autoPlay
                style={{ width: '100%', height: '100%', objectFit: 'contain' }}
              />
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseVideoModal}>Kapat</Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={saveFormOpen}
        onClose={() => !savingVideoUrl && setSaveFormOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Video Bilgilerini Girin</DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 2 }}>
            <TextField
              label="Başlık / Title"
              fullWidth
              value={saveFormData.title}
              onChange={(e) => setSaveFormData({ ...saveFormData, title: e.target.value })}
              required
            />
            <FormControl fullWidth>
              <InputLabel id="language-label">Dil / Language</InputLabel>
              <Select
                labelId="language-label"
                label="Dil / Language"
                value={saveFormData.languageCode}
                onChange={(e) => setSaveFormData({ ...saveFormData, languageCode: e.target.value })}
              >
                <MenuItem value="TR">TR - Turkish</MenuItem>
                <MenuItem value="EN">EN - English</MenuItem>
                <MenuItem value="DE">DE - German</MenuItem>
                <MenuItem value="ES">ES - Spanish</MenuItem>
                <MenuItem value="AR">AR - Arabic</MenuItem>
                <MenuItem value="RU">RU - Russian</MenuItem>
                <MenuItem value="ZH">ZH - Chinese</MenuItem>
                <MenuItem value="KO">KO - Korean</MenuItem>
                <MenuItem value="JA">JA - Japanese</MenuItem>
                <MenuItem value="FR">FR - French</MenuItem>
                <MenuItem value="PT">PT - Portuguese</MenuItem>
                <MenuItem value="IT">IT - Italian</MenuItem>
              </Select>
            </FormControl>
            <FormControl fullWidth>
              <InputLabel id="type-label">Video Tipi</InputLabel>
              <Select
                labelId="type-label"
                label="Video Tipi"
                value={saveFormData.type}
                onChange={(e) => setSaveFormData({ ...saveFormData, type: e.target.value })}
              >
                <MenuItem value="short">Short</MenuItem>
                <MenuItem value="youtube">YouTube</MenuItem>
              </Select>
            </FormControl>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setSaveFormOpen(false)} disabled={savingVideoUrl !== null}>
            İptal
          </Button>
          <Button
            variant="contained"
            onClick={handleSaveVideoToDatabase}
            disabled={!saveFormData.title || savingVideoUrl !== null}
          >
            {savingVideoUrl ? 'Kaydediliyor...' : 'Kaydet'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default VideoMergePage;
